import { Api } from '@/api/api.client';
import { usePdfExportOperation } from '@/composables/usePdfExportOperation';
import { useToast } from '@/composables/useToast';
import { openUrlInNewTab } from '@/utils/dom.utils';
import { computed, ComputedRef, ref } from 'vue';
import { useConfirmDialog } from '../useConfirmDialog';
import { EIcon } from 'common/enum/misc.enum';
import { useI18n } from 'vue-i18n';

interface IPerformanceAssessmentEntryPdfComposable {
  loading: ComputedRef<boolean>;
  startPdfOperation: (entryId: number, studentIds?: number[]) => Promise<void>;
}

export const usePerformanceAssessmentEntryPdf = (): IPerformanceAssessmentEntryPdfComposable => {
  const { customErrorToast } = useToast();
  const { confirm } = useConfirmDialog();
  const { t } = useI18n();
  const { runPdfExport } = usePdfExportOperation();

  const loadingState = ref(false);
  const loading = computed(() => loadingState.value);

  const startPdfOperation = async (entryId: number, studentIds?: number[]): Promise<void> => {
    const confirmed = await confirm({
      title: t('performanceAssessmentEntry.pdf.confirmTitle'),
      description: t('performanceAssessmentEntry.pdf.confirmDescription'),
      confirmButtonLabel: t('performanceAssessmentEntry.pdf.confirmButton'),
      declineButtonLabel: t('general.cancel'),
      theme: 'primary',
      size: 'lg',
      confirmButtonIcon: EIcon.PRINT,
    });

    if (!confirmed) return;

    loadingState.value = true;

    try {
      const result = await runPdfExport(() =>
        Api.Routes.PerformanceAssessmentEntry.startPdfExport({ id: entryId }, { studentIds }),
      );
      openUrlInNewTab(result.url);
    } catch (error) {
      console.error('Performance assessment entry PDF generation failed', error);
      customErrorToast(t('performanceAssessmentEntry.pdf.generationFailed'));
    } finally {
      loadingState.value = false;
    }
  };

  return {
    loading,
    startPdfOperation,
  };
};
